/**
 * Aba "Configuracoes": a regua editavel, toque por toque.
 *
 * Tudo em ESTADO LOCAL da previa: o rascunho vive aqui, e "Salvar na previa"
 * devolve a configuracao para a pagina, que passa a desenhar a regua da aba
 * "Visao geral" com a copy e os atrasos novos. Nada finge chamar rede -- o
 * rodape diz isso com todas as letras.
 *
 * Regras que o formulario IMPOE (e explica) -- sao regras de negocio, nao de
 * gosto:
 *  1. Nenhum toque antes do fato. No carrinho o atraso minimo e 30 min (antes
 *     disso a pessoa ainda esta comprando); nos expirados e 1 dia (ate o
 *     vencimento ela ainda e assinante). Um toque antes disso seria lembrete
 *     preventivo -- e nao ha recuperacao, nem comissao, sobre quem nao saiu.
 *     A linha do tempo no topo mostra a zona proibida em cinza.
 *  2. Toques em ordem crescente: o 2o vem depois do 1o.
 *  3. Variaveis obrigatorias presentes e nenhuma variavel desconhecida (um
 *     `{nome}` digitado errado chega ao cliente como texto cru).
 *  4. Limites do canal (1.024 caracteres no WhatsApp, 20 no botao): passar
 *     deles e a mensagem chegar cortada ou nem sair.
 * Erros bloqueiam o salvar; avisos (assunto longo) so avisam.
 *
 * A previa do aparelho (WhatsAppPreview / EmailPreview) fica AO LADO do campo,
 * lado a lado uma da outra em tela larga, e reage a cada tecla: quem escreve
 * a copy ve na hora onde a variavel cai, se o botao coube e quantos
 * caracteres sobram. O atraso e editado em unidade natural (minutos, horas ou
 * dias) e guardado sempre em minutos, que e o que o motor entende.
 */

import { useEffect, useRef, useState } from 'react';
import {
  Badge,
  Button,
  EmailPreview,
  Field,
  Fieldset,
  Footnote,
  Icon,
  Input,
  Panel,
  SectionCard,
  Select,
  Textarea,
  Toggle,
  WhatsAppPreview,
  destacarVariaveis,
} from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import type { EmailCopy, FunnelKind, TouchButton, TouchDefinition } from '../types';
import {
  CANAL,
  LIMITES,
  MINUTOS_POR_UNIDADE,
  UNIDADE_LABEL,
  VOCABULARIO,
  rotuloDoAtraso,
  rotuloDoMarco,
  unidadeNatural,
  variaveisDaCopy,
  variaveisNoTexto,
  type UnidadeDeAtraso,
  type VariavelDaCopy,
} from '../vocabulario';

export interface ConfigDaRegua {
  readonly ativo: boolean;
  readonly touches: readonly TouchDefinition[];
}

/** Botoes sugeridos ao acrescentar um botao: um de link e um de resposta, no vocabulario do funil. */
function botoesPadrao(kind: FunnelKind): readonly TouchButton[] {
  return kind === 'expirados'
    ? [
        { label: 'Reativar acesso', kind: 'link' },
        { label: 'Tenho uma dúvida', kind: 'reply' },
      ]
    : [
        { label: 'Finalizar compra', kind: 'link' },
        { label: 'Tenho uma dúvida', kind: 'reply' },
      ];
}

const UNIDADES: readonly UnidadeDeAtraso[] = ['min', 'h', 'd'];

function iguais(a: ConfigDaRegua, b: ConfigDaRegua): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

interface Diagnostico {
  readonly erros: Readonly<Record<string, string>>;
  readonly avisos: Readonly<Record<string, string>>;
}

function faltamObrigatorias(texto: string, variaveis: readonly VariavelDaCopy[]): readonly string[] {
  const presentes = new Set(variaveisNoTexto(texto));
  return variaveis.filter((v) => v.papel === 'obrigatoria' && !presentes.has(v.nome)).map((v) => v.nome);
}

function desconhecidas(texto: string, variaveis: readonly VariavelDaCopy[]): readonly string[] {
  const conhecidas = new Set(variaveis.map((v) => v.nome));
  return variaveisNoTexto(texto).filter((nome) => !conhecidas.has(nome));
}

function validar(kind: FunnelKind, config: ConfigDaRegua): Diagnostico {
  const v = VOCABULARIO[kind];
  const erros: Record<string, string> = {};
  const avisos: Record<string, string> = {};
  const ordenados = [...config.touches].sort((a, b) => a.order - b.order);

  ordenados.forEach((t, i) => {
    const anterior = ordenados[i - 1];
    const temBotaoDeLink = t.buttons.some((b) => b.kind === 'link');
    const canalDaCopy = t.channel === 'voice' ? 'voice' : 'whatsapp';
    const variaveis = variaveisDaCopy(kind, canalDaCopy, temBotaoDeLink);

    // 1. Depois do fato, sempre.
    if (t.delayMinutes < v.atrasoMinimoMinutos) {
      erros[`${t.id}:delay`] =
        kind === 'expirados'
          ? `Antes de ${v.atrasoMinimoTexto} depois do vencimento a pessoa ainda é assinante: um toque aqui seria aviso de renovação, não recuperação — e não gera comissão.`
          : `Antes de ${v.atrasoMinimoTexto} sem pagar a pessoa ainda não abandonou: um toque aqui seria lembrete, não recuperação — e não gera comissão.`;
    } else if (anterior !== undefined && t.delayMinutes <= anterior.delayMinutes) {
      // 2. Ordem crescente.
      erros[`${t.id}:delay`] = `Precisa vir depois do toque anterior (${rotuloDoAtraso(kind, anterior.delayMinutes)}).`;
    }

    // 3. Copy.
    if (t.body.trim() === '') {
      erros[`${t.id}:body`] = t.channel === 'voice' ? 'Escreva o roteiro da ligação.' : 'Escreva a mensagem.';
    } else {
      const faltam = faltamObrigatorias(t.body, variaveis);
      const estranhas = desconhecidas(t.body, variaveis);
      const limite = t.channel === 'voice' ? LIMITES.roteiroVoz : LIMITES.whatsappCorpo;
      if (estranhas.length > 0) {
        erros[`${t.id}:body`] = `${estranhas.join(', ')} não ${estranhas.length === 1 ? 'existe' : 'existem'}: chegaria ao cliente como texto cru. Use só as variáveis da lista.`;
      } else if (faltam.length > 0) {
        erros[`${t.id}:body`] = `Falta ${faltam.join(' e ')}${faltam.includes('{link}') ? ' — sem botão de link, é o único caminho para pagar' : ''}.`;
      } else if (t.body.length > limite) {
        erros[`${t.id}:body`] = `${formatCount(t.body.length)} caracteres; o limite ${t.channel === 'voice' ? 'do roteiro' : 'do WhatsApp'} é ${formatCount(limite)}.`;
      }
    }

    // 4. Botoes.
    t.buttons.forEach((b, j) => {
      if (b.label.trim() === '') {
        erros[`${t.id}:btn:${j}`] = 'Dê um nome ao botão.';
      } else if (b.label.length > LIMITES.whatsappBotao) {
        erros[`${t.id}:btn:${j}`] = `O WhatsApp corta botões com mais de ${LIMITES.whatsappBotao} caracteres.`;
      }
    });

    // 5. E-mail.
    if (t.email !== null) {
      const variaveisEmail = variaveisDaCopy(kind, 'email', false);
      if (t.email.subject.trim() === '') {
        erros[`${t.id}:subject`] = 'O e-mail precisa de assunto.';
      } else if (desconhecidas(t.email.subject, variaveisEmail).length > 0) {
        erros[`${t.id}:subject`] = `${desconhecidas(t.email.subject, variaveisEmail).join(', ')} não existe.`;
      } else if (t.email.subject.length > LIMITES.emailAssunto) {
        avisos[`${t.id}:subject`] = `Assuntos acima de ${LIMITES.emailAssunto} caracteres aparecem cortados na caixa de entrada.`;
      }
      if (t.email.body.trim() === '') {
        erros[`${t.id}:ebody`] = 'Escreva o corpo do e-mail.';
      } else {
        const faltam = faltamObrigatorias(t.email.body, variaveisEmail);
        const estranhas = desconhecidas(t.email.body, variaveisEmail);
        if (estranhas.length > 0) {
          erros[`${t.id}:ebody`] = `${estranhas.join(', ')} não ${estranhas.length === 1 ? 'existe' : 'existem'}.`;
        } else if (faltam.length > 0) {
          erros[`${t.id}:ebody`] = `Falta ${faltam.join(' e ')} no corpo do e-mail.`;
        }
      }
      if (t.email.preheader.length > LIMITES.emailPreheader) {
        avisos[`${t.id}:preheader`] = `Acima de ${LIMITES.emailPreheader} caracteres o pré-cabeçalho é cortado.`;
      }
    }
  });

  return { erros, avisos };
}

/** "312 / 1.024" com cor de aviso perto do limite e de erro acima dele; sempre com texto. */
function Contador({ atual, limite, rotulo }: { readonly atual: number; readonly limite: number; readonly rotulo: string }): JSX.Element {
  const acima = atual > limite;
  const perto = !acima && atual > limite * 0.9;
  return (
    <span className={`tabular-nums ${acima ? 'font-semibold text-status-critical' : perto ? 'text-status-warning' : 'text-ink-subtle'}`} aria-live="polite">
      {formatCount(atual)} / {formatCount(limite)} {rotulo}
      {acima ? ' — acima do limite' : perto ? ' — perto do limite' : ''}
    </span>
  );
}

/** Checklist das variaveis: qual esta no texto, qual falta, qual e obrigatoria. */
function ChecklistDeVariaveis({ texto, variaveis, titulo }: { readonly texto: string; readonly variaveis: readonly VariavelDaCopy[]; readonly titulo: string }): JSX.Element {
  const presentes = new Set(variaveisNoTexto(texto));
  return (
    <ul className="flex flex-wrap gap-1.5" aria-label={titulo}>
      {variaveis.map((va) => {
        const tem = presentes.has(va.nome);
        const tone = tem ? 'good' : va.papel === 'obrigatoria' ? 'critical' : 'neutral';
        return (
          <li key={va.nome} title={va.porQue}>
            <Badge tone={tone} icon={tem ? 'check' : va.papel === 'obrigatoria' ? 'alerta' : undefined}>
              {va.nome}
              <span className="font-normal opacity-80">{tem ? '' : va.papel === 'obrigatoria' ? ' · obrigatória' : va.papel === 'recomendada' ? ' · recomendada' : ' · opcional'}</span>
            </Badge>
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Linha do tempo da regua: T0 (o fato) a esquerda, cada toque como um marco a
 * direita, e a ZONA ANTES DO FATO hachurada -- ali nao existe toque. Espaco
 * igual entre marcos (os atrasos vao de 30 min a 90 dias; uma escala real
 * esmagaria os primeiros), com o intervalo escrito em cada marco.
 */
function LinhaDoTempoDaRegua({ kind, touches, erros }: { readonly kind: FunnelKind; readonly touches: readonly TouchDefinition[]; readonly erros: Readonly<Record<string, string>> }): JSX.Element {
  const v = VOCABULARIO[kind];
  const ordenados = [...touches].sort((a, b) => a.delayMinutes - b.delayMinutes);
  return (
    <Panel className="px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Icon name="relogio" size="sm" className="text-ink-muted" />
          Linha do tempo da régua
        </h3>
        <Badge tone="neutral" icon="escudo">
          Só depois do {v.fato}
        </Badge>
      </div>
      <ol className="mt-4 flex items-stretch gap-0 overflow-x-auto pb-1" aria-label="Marcos da régua em ordem">
        <li className="flex min-w-[9rem] shrink-0 flex-col items-center gap-1 text-center">
          <span
            aria-hidden="true"
            className="h-6 w-full rounded-l-lg border border-dashed border-edge"
            style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 6px, rgb(var(--color-ink) / 0.06) 6px 12px)' }}
          />
          <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Antes do {v.fato}</span>
          <span className="text-xs text-ink-subtle">nenhum toque</span>
        </li>
        <li className="flex min-w-[7rem] shrink-0 flex-col items-center gap-1 text-center">
          <span className="flex h-6 w-full items-center">
            <span aria-hidden="true" className="h-3 w-3 rounded-full border-2 border-brand bg-brand" />
            <span aria-hidden="true" className="h-0.5 flex-1 bg-line" />
          </span>
          <span className="text-[10px] font-semibold uppercase tracking-wide text-brand">T0 · {v.fatoRotulo}</span>
          <span className="text-xs text-ink-subtle">{kind === 'expirados' ? 'fim da assinatura' : '30 min sem pagar'}</span>
        </li>
        {ordenados.map((t, i) => {
          const anterior = ordenados[i - 1];
          const gap = t.delayMinutes - (anterior?.delayMinutes ?? 0);
          const canal = t.channel === 'voice' ? CANAL.voice : CANAL.whatsapp;
          const comErro = erros[`${t.id}:delay`] !== undefined;
          return (
            <li key={t.id} className="flex min-w-[8rem] shrink-0 flex-col items-center gap-1 text-center">
              <span className="flex h-6 w-full items-center">
                <span aria-hidden="true" className="h-0.5 flex-1 bg-line" />
                <span aria-hidden="true" className={`grid h-6 w-6 place-items-center rounded-full border ${comErro ? 'border-status-critical bg-status-critical/10 text-status-critical' : t.enabled ? 'border-line bg-surface text-ink-muted' : 'border-dashed border-edge bg-surface text-ink-subtle'}`}>
                  <Icon name={canal.icon} size="xs" />
                </span>
                <span aria-hidden="true" className={`h-0.5 flex-1 ${i === ordenados.length - 1 ? 'bg-transparent' : 'bg-line'}`} />
              </span>
              <a href={`#config-${t.id}`} className={`text-[10px] font-semibold uppercase tracking-wide underline-offset-2 hover:underline ${comErro ? 'text-status-critical' : 'text-ink'}`}>
                {rotuloDoAtraso(kind, t.delayMinutes)}
                {comErro ? <span className="sr-only"> (com erro no atraso)</span> : null}
              </a>
              <span className="text-xs text-ink-subtle">{i === 0 ? `${rotuloDoAtraso(kind, gap).replace(/^[TD]\+/, '+')} depois` : `${rotuloDoAtraso(kind, gap).replace(/^[TD]\+/, '+')} após o anterior`}</span>
            </li>
          );
        })}
      </ol>
      <div className="mt-3">
        <Footnote>{v.notaDepoisDoFato}</Footnote>
      </div>
    </Panel>
  );
}

export function ConfiguracaoDaRegua({
  kind,
  salvo,
  empresa,
  onSalvar,
  focoToqueId,
}: {
  readonly kind: FunnelKind;
  readonly salvo: ConfigDaRegua;
  readonly empresa: string;
  readonly onSalvar: (config: ConfigDaRegua) => void;
  /** Toque que deve ganhar foco/rolagem ao abrir a aba (vem de "O que fazer agora"). */
  readonly focoToqueId?: string | null;
}): JSX.Element {
  const v = VOCABULARIO[kind];
  const [draft, setDraft] = useState<ConfigDaRegua>(salvo);
  const [unidades, setUnidades] = useState<Readonly<Record<string, UnidadeDeAtraso>>>(() =>
    Object.fromEntries(salvo.touches.map((t) => [t.id, unidadeNatural(t.delayMinutes)])),
  );
  const [salvoEm, setSalvoEm] = useState<string | null>(null);
  const raiz = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (focoToqueId === undefined || focoToqueId === null) {
      return;
    }
    const alvo = raiz.current?.querySelector<HTMLElement>(`#config-${focoToqueId}`);
    alvo?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    alvo?.querySelector<HTMLElement>('textarea')?.focus({ preventScroll: true });
  }, [focoToqueId]);

  const alterado = !iguais(draft, salvo);
  const { erros, avisos } = validar(kind, draft);
  const quantosErros = Object.keys(erros).length;
  const temErro = quantosErros > 0;

  const atualizarToque = (id: string, patch: Partial<TouchDefinition>): void => {
    setDraft((d) => ({ ...d, touches: d.touches.map((t) => (t.id === id ? { ...t, ...patch } : t)) }));
  };
  const atualizarEmail = (id: string, patch: Partial<EmailCopy>): void => {
    setDraft((d) => ({ ...d, touches: d.touches.map((t) => (t.id === id && t.email !== null ? { ...t, email: { ...t.email, ...patch } } : t)) }));
  };
  const atualizarBotao = (id: string, indice: number, patch: Partial<TouchButton>): void => {
    setDraft((d) => ({
      ...d,
      touches: d.touches.map((t) => (t.id === id ? { ...t, buttons: t.buttons.map((b, i) => (i === indice ? { ...b, ...patch } : b)) } : t)),
    }));
  };
  const definirQuantidadeDeBotoes = (id: string, quantidade: number): void => {
    setDraft((d) => ({
      ...d,
      touches: d.touches.map((t) => {
        if (t.id !== id) {
          return t;
        }
        const sugeridos = botoesPadrao(kind);
        const novos: TouchButton[] = [];
        for (let i = 0; i < quantidade; i += 1) {
          novos.push(t.buttons[i] ?? sugeridos[i] ?? { label: 'Botão', kind: 'link' });
        }
        return { ...t, buttons: novos };
      }),
    }));
  };

  const salvar = (): void => {
    if (temErro) {
      return;
    }
    onSalvar(draft);
    setSalvoEm(new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' }).format(new Date()));
  };
  const descartar = (): void => {
    setDraft(salvo);
    setUnidades(Object.fromEntries(salvo.touches.map((t) => [t.id, unidadeNatural(t.delayMinutes)])));
  };

  return (
    <div ref={raiz} className="flex flex-col gap-6">
      <Panel tone={draft.ativo ? 'good' : 'warning'} className="px-5 py-4">
        <Toggle
          checked={draft.ativo}
          onChange={(ativo) => setDraft((d) => ({ ...d, ativo }))}
          label={
            <span className="flex items-center gap-2">
              <Icon name="robo" size="sm" className="text-ink-muted" />
              Disparo automático
              <Badge tone={draft.ativo ? 'good' : 'warning'} dot uppercase>
                {draft.ativo ? 'Ligado' : 'Desligado'}
              </Badge>
            </span>
          }
          description="Desligar pausa todos os toques deste funil. Quem já está na régua fica esperando até religar; ninguém sai da fila por causa disso."
        />
      </Panel>

      <LinhaDoTempoDaRegua kind={kind} touches={draft.touches} erros={erros} />

      {draft.touches.map((t) => {
        const canal = t.channel === 'voice' ? CANAL.voice : CANAL.whatsapp;
        const unidade = unidades[t.id] ?? unidadeNatural(t.delayMinutes);
        const fator = MINUTOS_POR_UNIDADE[unidade];
        const valorDoAtraso = Math.max(1, Math.round(t.delayMinutes / fator));
        const voz = t.channel === 'voice';
        const temBotaoDeLink = t.buttons.some((b) => b.kind === 'link');
        const variaveisCorpo = variaveisDaCopy(kind, voz ? 'voice' : 'whatsapp', temBotaoDeLink);
        const variaveisEmail = variaveisDaCopy(kind, 'email', false);
        const limiteCorpo = voz ? LIMITES.roteiroVoz : LIMITES.whatsappCorpo;
        const errosDoToque = Object.keys(erros).filter((k) => k.startsWith(`${t.id}:`)).length;

        return (
          <SectionCard
            key={t.id}
            id={`config-${t.id}`}
            title={`${rotuloDoAtraso(kind, t.delayMinutes)} · ${canal.label}`}
            icon={canal.icon}
            description={rotuloDoMarco(kind, t.order, t.delayMinutes, t.isLast)}
            aside={
              <>
                {errosDoToque > 0 ? (
                  <Badge tone="critical" icon="alerta">
                    {errosDoToque} {errosDoToque === 1 ? 'problema' : 'problemas'}
                  </Badge>
                ) : null}
                <Toggle checked={t.enabled} onChange={(enabled) => atualizarToque(t.id, { enabled })} label="Toque ligado" />
              </>
            }
            className={t.enabled ? '' : 'opacity-80'}
          >
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] 2xl:grid-cols-[minmax(0,1fr)_44rem]">
              <div className="flex flex-col gap-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field
                    label={`Atraso depois do ${v.fato}`}
                    hint={`Contado da entrada no funil, não do toque anterior. Mínimo: ${v.atrasoMinimoTexto} — antes disso a pessoa ainda não ${kind === 'expirados' ? 'venceu' : 'abandonou'}.`}
                    error={erros[`${t.id}:delay`] ?? null}
                  >
                    {({ id, describedBy, invalid }) => (
                      <div className="flex gap-2">
                        <Input
                          id={id}
                          type="number"
                          min={1}
                          inputMode="numeric"
                          aria-describedby={describedBy}
                          aria-invalid={invalid}
                          value={valorDoAtraso}
                          onChange={(e) => {
                            // Campo vazio ou zero cai em 1 unidade: o motor nao aceita atraso nulo e
                            // um campo "0" ou vazio na tela seria uma promessa falsa. O minimo de
                            // negocio (30 min / 1 dia) e cobrado pela validacao, com explicacao.
                            const n = Number.parseInt(e.target.value, 10);
                            atualizarToque(t.id, { delayMinutes: Number.isFinite(n) && n >= 1 ? n * fator : fator });
                          }}
                          className="w-28"
                        />
                        <Select
                          aria-label="Unidade do atraso"
                          value={unidade}
                          onChange={(e) => {
                            const nova = UNIDADES.find((u) => u === e.target.value) ?? unidade;
                            setUnidades((u) => ({ ...u, [t.id]: nova }));
                            atualizarToque(t.id, { delayMinutes: valorDoAtraso * MINUTOS_POR_UNIDADE[nova] });
                          }}
                        >
                          {UNIDADES.map((u) => (
                            <option key={u} value={u}>
                              {UNIDADE_LABEL[u]}
                            </option>
                          ))}
                        </Select>
                      </div>
                    )}
                  </Field>

                  {voz ? (
                    <Field label="Tentativas de ligação" hint="Teto de 2 por pessoa (RF08). Caixa postal encerra a tentativa.">
                      {({ id, describedBy }) => (
                        <Select id={id} aria-describedby={describedBy} value={String(t.maxAttempts)} onChange={(e) => atualizarToque(t.id, { maxAttempts: e.target.value === '2' ? 2 : 1 })}>
                          <option value="1">1 tentativa</option>
                          <option value="2">2 tentativas</option>
                        </Select>
                      )}
                    </Field>
                  ) : (
                    <Field label="Botões da mensagem" hint="Até 2. Sem botão de link, o {link} no texto passa a ser obrigatório.">
                      {({ id, describedBy }) => (
                        <Select id={id} aria-describedby={describedBy} value={String(t.buttons.length)} onChange={(e) => definirQuantidadeDeBotoes(t.id, Math.min(2, Math.max(0, Number.parseInt(e.target.value, 10) || 0)))}>
                          <option value="0">Sem botão</option>
                          <option value="1">1 botão</option>
                          <option value="2">2 botões</option>
                        </Select>
                      )}
                    </Field>
                  )}
                </div>

                <Field
                  label={voz ? 'Roteiro da IA (resumo)' : 'Mensagem de WhatsApp'}
                  hint={
                    <span className="flex flex-wrap items-center justify-between gap-2">
                      <span>Um Enter separa parágrafos.</span>
                      <Contador atual={t.body.length} limite={limiteCorpo} rotulo="caracteres" />
                    </span>
                  }
                  error={erros[`${t.id}:body`] ?? null}
                  required
                >
                  {({ id, describedBy, invalid }) => (
                    <Textarea id={id} aria-describedby={describedBy} aria-invalid={invalid} rows={5} value={t.body} onChange={(e) => atualizarToque(t.id, { body: e.target.value })} />
                  )}
                </Field>
                <ChecklistDeVariaveis texto={t.body} variaveis={variaveisCorpo} titulo={`Variáveis da ${voz ? 'ligação' : 'mensagem'}`} />

                {voz
                  ? null
                  : t.buttons.map((b, i) => (
                      <div key={`${t.id}-botao-${i}`} className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_16rem]">
                        <Field
                          label={`Texto do botão ${i + 1}`}
                          hint={<Contador atual={b.label.length} limite={LIMITES.whatsappBotao} rotulo="caracteres" />}
                          error={erros[`${t.id}:btn:${i}`] ?? null}
                          required
                        >
                          {({ id, describedBy, invalid }) => (
                            <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} maxLength={LIMITES.whatsappBotao + 5} value={b.label} onChange={(e) => atualizarBotao(t.id, i, { label: e.target.value })} />
                          )}
                        </Field>
                        <Field label="O que ele faz">
                          {({ id, describedBy }) => (
                            <Select id={id} aria-describedby={describedBy} value={b.kind} onChange={(e) => atualizarBotao(t.id, i, { kind: e.target.value === 'reply' ? 'reply' : 'link' })}>
                              <option value="link">{kind === 'expirados' ? 'Abre a reativação (link)' : 'Abre o checkout (link)'}</option>
                              <option value="reply">Resposta pro comercial (para a régua)</option>
                            </Select>
                          )}
                        </Field>
                      </div>
                    ))}

                {t.email === null ? null : (
                  <Fieldset legend="E-mail que sai junto" description="Sai no mesmo instante do WhatsApp. Quando o WhatsApp não entrega, ele vira o canal de reserva.">
                    <Field
                      label="Assunto"
                      hint={
                        <span className="flex flex-wrap items-center justify-between gap-2">
                          <span className={avisos[`${t.id}:subject`] === undefined ? '' : 'text-status-warning'}>{avisos[`${t.id}:subject`] ?? 'Curto e com o nome da pessoa funciona melhor.'}</span>
                          <Contador atual={t.email.subject.length} limite={LIMITES.emailAssunto} rotulo="caracteres" />
                        </span>
                      }
                      error={erros[`${t.id}:subject`] ?? null}
                      required
                    >
                      {({ id, describedBy, invalid }) => (
                        <Input id={id} aria-describedby={describedBy} aria-invalid={invalid} value={t.email?.subject ?? ''} onChange={(e) => atualizarEmail(t.id, { subject: e.target.value })} />
                      )}
                    </Field>
                    <Field
                      label="Pré-cabeçalho"
                      hint={
                        <span className="flex flex-wrap items-center justify-between gap-2">
                          <span className={avisos[`${t.id}:preheader`] === undefined ? '' : 'text-status-warning'}>{avisos[`${t.id}:preheader`] ?? 'A linha cinza que aparece na caixa de entrada, depois do assunto.'}</span>
                          <Contador atual={t.email.preheader.length} limite={LIMITES.emailPreheader} rotulo="caracteres" />
                        </span>
                      }
                    >
                      {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} value={t.email?.preheader ?? ''} onChange={(e) => atualizarEmail(t.id, { preheader: e.target.value })} />}
                    </Field>
                    <Field label="Corpo" error={erros[`${t.id}:ebody`] ?? null} required hint="O {link} precisa estar no texto: nem todo leitor de e-mail mostra o botão.">
                      {({ id, describedBy, invalid }) => (
                        <Textarea id={id} aria-describedby={describedBy} aria-invalid={invalid} rows={6} value={t.email?.body ?? ''} onChange={(e) => atualizarEmail(t.id, { body: e.target.value })} />
                      )}
                    </Field>
                    <ChecklistDeVariaveis texto={t.email.body} variaveis={variaveisEmail} titulo="Variáveis do e-mail" />
                    <Field label="Texto do botão do e-mail">
                      {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} maxLength={40} value={t.email?.cta ?? ''} onChange={(e) => atualizarEmail(t.id, { cta: e.target.value })} />}
                    </Field>
                  </Fieldset>
                )}
              </div>

              <div className="flex flex-col gap-3">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Como chega · lado a lado</p>
                <div className={`grid gap-4 ${t.email === null ? '' : 'sm:grid-cols-2 lg:grid-cols-1 2xl:grid-cols-2'}`}>
                  <figure className="flex min-w-0 flex-col gap-2">
                    {voz ? (
                      <Panel tone="accent" className="px-4 py-4 text-sm">
                        <p className="mb-2 flex items-center gap-1.5 font-medium text-ink">
                          <Icon name="robo" size="sm" />
                          Ligação da IA · até {t.maxAttempts} {t.maxAttempts === 1 ? 'tentativa' : 'tentativas'}
                        </p>
                        <p className="whitespace-pre-wrap leading-relaxed text-ink-muted">{destacarVariaveis(t.body)}</p>
                      </Panel>
                    ) : (
                      <WhatsAppPreview from={empresa} body={t.body} buttons={t.buttons} />
                    )}
                    <figcaption className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-ink-subtle">
                      <span className="inline-flex items-center gap-1">
                        <Icon name={canal.icon} size="xs" />
                        {canal.label}
                      </span>
                      <Contador atual={t.body.length} limite={limiteCorpo} rotulo="" />
                      <span>
                        variáveis obrigatórias: {formatCount(variaveisCorpo.filter((va) => va.papel === 'obrigatoria').length - faltamObrigatorias(t.body, variaveisCorpo).length)} de{' '}
                        {formatCount(variaveisCorpo.filter((va) => va.papel === 'obrigatoria').length)}
                      </span>
                    </figcaption>
                  </figure>
                  {t.email === null ? null : (
                    <figure className="flex min-w-0 flex-col gap-2">
                      <EmailPreview from={empresa} subject={t.email.subject} preheader={t.email.preheader} body={t.email.body} cta={t.email.cta} />
                      <figcaption className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-ink-subtle">
                        <span className="inline-flex items-center gap-1">
                          <Icon name="email" size="xs" />
                          E-mail
                        </span>
                        <span>
                          assunto <Contador atual={t.email.subject.length} limite={LIMITES.emailAssunto} rotulo="" />
                        </span>
                        <span>
                          variáveis obrigatórias: {formatCount(variaveisEmail.filter((va) => va.papel === 'obrigatoria').length - faltamObrigatorias(t.email.body, variaveisEmail).length)} de{' '}
                          {formatCount(variaveisEmail.filter((va) => va.papel === 'obrigatoria').length)}
                        </span>
                      </figcaption>
                    </figure>
                  )}
                </div>
              </div>
            </div>
          </SectionCard>
        );
      })}

      <div className="sticky bottom-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-line bg-elevated px-5 py-4 shadow-float">
        <p className="text-sm text-ink-muted" role="status">
          {temErro ? (
            <span className="text-status-critical">
              {formatCount(quantosErros)} {quantosErros === 1 ? 'problema impede' : 'problemas impedem'} salvar.
            </span>
          ) : alterado ? (
            'Há alterações não salvas.'
          ) : (
            'Tudo salvo na prévia.'
          )}{' '}
          <span className="text-ink-subtle">Nada aqui chega ao servidor ainda: é uma prévia.</span>
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {salvoEm !== null && !alterado ? (
            <span role="status">
              <Badge tone="good" icon="check">
                Salvo na prévia às {salvoEm}
              </Badge>
            </span>
          ) : null}
          {alterado ? (
            <Button variant="secondary" onClick={descartar}>
              Descartar alterações
            </Button>
          ) : null}
          <Button variant="primary" icon="check" disabled={!alterado || temErro} onClick={salvar}>
            Salvar na prévia
          </Button>
        </div>
      </div>
    </div>
  );
}
